<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'
import { useAppearanceStore } from '@stores/appearance'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    playlist: {
        type: Object,
        default: () => {},
    },
    // D9: playlist-info.vue has always passed `false` here (the playlist page) — the
    // same "already on the page → no view row" rule the other five menus apply.
    hasViewPlaylistOption: {
        type: Boolean,
        default: true,
    },
})

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()

const routeName = computed(() => (props.playlist.type === 'chart' ? 'browse/charts' : 'browse/playlists'))

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.playlist.id) return ''

    const href = router.resolve({ name: routeName.value, params: { id: props.playlist.id } }).href
    return siteUrl(href)
})

const kind = computed(() => (props.playlist.type === 'chart' ? 'Chart' : 'Playlist'))

const header = computed(() => ({
    image: props.playlist,
    imageType: 'revisions_album_art',
    title: props.playlist.revisions_title,
    subtitle: kind.value,
}))

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// The View row is hidden without an id: its router-link would throw resolving the param.
const items = computed(() => [
    {
        id: 'view',
        label: `View ${kind.value}`,
        to: { name: routeName.value, params: { id: props.playlist.id } },
        hidden: !props.hasViewPlaylistOption || !props.playlist.id,
    },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.playlist.revisions_title} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: `Copy ${kind.value} Link`, action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="playlist-options"
        :items="items"
        :header="header"
        trigger-label="Playlist options"
    >
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>
